import React from "react";
import { FieldDisplay, JSONViewer, StatusBadge } from "../../../ui";
import { formatOptionalDate } from "../../../lib/date_utils";
import { TraceDisplayWithToggle } from "./TraceDisplayWithToggle";
import type {
  SourceDetailResponse,
  SourceDetailDocument,
  SourceDetailMemory,
  SourceDetailAnnotation,
  SourceDetailUploadedEvaluation,
} from "../types";

function statusBadgeVariant(status: string): "success" | "error" | "neutral" {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  return "neutral";
}

export function SourceDetailView({ data }: { data: SourceDetailResponse }) {
  switch (data.type) {
    case "document":
      return <DocumentContent data={data} />;
    case "memory":
      return <MemoryContent data={data} />;
    case "annotation":
      return <AnnotationContent data={data} />;
    case "uploaded_evaluation":
      return <UploadedEvaluationContent data={data} />;
    default:
      return <JSONViewer data={data as Record<string, any>} />;
  }
}

function DocumentContent({ data }: { data: SourceDetailDocument }) {
  return (
    <div className="space-y-3">
      <FieldDisplay label="Filename" value={data.filename} valueClassName="font-mono text-foreground" />
      <FieldDisplay
        label="Status"
        value={<StatusBadge variant={statusBadgeVariant(data.status)}>{data.status}</StatusBadge>}
      />
      <FieldDisplay label="Type" value={data.document_type} />
      <FieldDisplay label="Created" value={formatOptionalDate(data.created_at)} valueClassName="text-muted-foreground" />
    </div>
  );
}

function MemoryContent({ data }: { data: SourceDetailMemory }) {
  return (
    <div className="space-y-3">
      {data.score !== null && (
        <FieldDisplay
          label="Score"
          value={
            <StatusBadge variant={data.score >= 0.5 ? "success" : "error"}>
              {data.score.toFixed(2)}
            </StatusBadge>
          }
        />
      )}
      <FieldDisplay label="Criteria" value={data.criteria} />
      {data.explanation && (
        <FieldDisplay
          label="Explanation"
          value={data.explanation}
          valueClassName="text-muted-foreground whitespace-pre-wrap"
        />
      )}
      <div className="mt-1">
        <TraceDisplayWithToggle trace={data.trace} />
      </div>
      {data.created_at && (
        <FieldDisplay label="Created" value={formatOptionalDate(data.created_at)} valueClassName="text-muted-foreground" />
      )}
    </div>
  );
}

function AnnotationContent({ data }: { data: SourceDetailAnnotation }) {
  return (
    <div className="space-y-3">
      <div className="mt-1">
        <TraceDisplayWithToggle trace={data.trace} />
      </div>
      {data.comments.length > 0 && (
        <div>
          <label className="block text-sm font-semibold text-muted-foreground mb-1">
            Comments ({data.comments.length})
          </label>
          <ul className="mt-1 space-y-2">
            {data.comments.map((c) => (
              <li
                key={c.id}
                className="rounded border border-border bg-card p-3 text-sm text-foreground"
              >
                <p className="whitespace-pre-wrap">{c.comment}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatOptionalDate(c.created_at)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadedEvaluationContent({ data }: { data: SourceDetailUploadedEvaluation }) {
  return (
    <div className="space-y-3">
      <FieldDisplay
        label="Rating"
        value={`${data.rating} / ${data.rating_out_of}`}
        valueClassName="font-semibold text-foreground"
      />
      {data.criteria && <FieldDisplay label="Criteria" value={data.criteria} />}
      {data.explanation && (
        <FieldDisplay
          label="Explanation"
          value={data.explanation}
          valueClassName="text-muted-foreground whitespace-pre-wrap"
        />
      )}
      <div className="mt-1">
        <TraceDisplayWithToggle trace={data.trace} />
      </div>
      {data.created_at && (
        <FieldDisplay label="Created" value={formatOptionalDate(data.created_at)} valueClassName="text-muted-foreground" />
      )}
    </div>
  );
}
